import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import { ChakraProvider, Icon } from '@chakra-ui/react';
import { UserProvider } from './utils/userContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { I18nextProvider } from 'react-i18next'
import i18n from './i18n';
import Layout from "./components/Layout";
import { BiSolidTachometer } from "react-icons/bi";
import { MdBusinessCenter, MdPeopleAlt } from "react-icons/md";
import { TbSortAZ } from "react-icons/tb";
import Provider from './pages/Provider';
import IsAdmin from './components/security/IsAdmin';
import { FaKey } from 'react-icons/fa';
import Roles from './pages/Admin/Roles';
import Users from './pages/Admin/Users';
import Companies from './pages/Companies';
import CategoriesPage from './pages/Products/Categories';
import SingleCategoryPage from './pages/Products/Categories/SingleCategoryPage';
import Products from './pages/Products/Products';
import Product from './pages/Products/Product';
import { FaBoxesPacking } from "react-icons/fa6";
import Inventory from './pages/Inventory';
import Bills from './pages/Bills/Bills';

import PlaceOfBusiness from './pages/Inventory/PlaceOfBussiness';
import AccessRestrictedPage from './pages/403';
import DontHavePermissionPage from './pages/401';
import { Suspense } from 'react';
import { PiVaultFill } from 'react-icons/pi';
import CustomValues from './pages/Admin/CustomValues';

const NormalLinks = [
  {
    name: "Dashboard",
    link: "/dashboard",
    icon: <Icon as={BiSolidTachometer} color='green.500' w={6} h={6}/>,
    permissions: []
  },
  { 
    name: "Provider",
    link: "/provider",
    icon: <Icon as={MdPeopleAlt} color='green.500' w={6} h={6}/>,
    permissions: [ "ViewProviders","EditProviders","CreateProviders","DeleteProviders"]
  },
  {
    name: "CATEGORIES_PAGE",
    link: "/categories",
    icon: <Icon as={TbSortAZ} color='green.500' w={6} h={6} />,
    permissions: ["CreateCategories","EditCategories","DeleteCategories","ViewCategories"]
  },
  {
    name: "Products",
    link: "/products",
    icon: <Icon as={MdPeopleAlt} color='green.500' w={6} h={6}/>,
    permissions: [ "ViewProducts","EditProducts","CreateProducts","DeleteProducts"]
  },
  {
    name: "Inventories",
    link: "/inventories",
    icon: <Icon as={FaBoxesPacking} color='green.500' w={6} h={6}/>,
    permissions: [  "CreateInventory","EditInventory","DeleteInventory","ViewInventory"]
  },
  {
    name: "Bills",
    link: "/bills",
    icon: <Icon as={MdPeopleAlt} color='green.500' w={6} h={6}/>,
    permissions: ["ViewBills","EditBills","CreateBills","DeleteBills"]
  },
];

const AdminLinks = [
  {
    name: "Dashboard",
    link: "/dashboard",
    icon: <Icon as={BiSolidTachometer} color='green.500' w={6} h={6}/>,
    permissions: []
    
  },
  {
    name: "Users",
    link: "/admin/users",
    icon: <Icon as={MdPeopleAlt} color='green.500' w={6} h={6}/>,
    permissions: ["CreateUsers","EditUsers","DeleteUsers","ViewUsers"]
  },
  {
    name: "Roles",
    link: "/admin/roles",
    icon: <Icon as={FaKey} color='green.500' w={6} h={6}/>,
    permissions: ["CreateRoles","EditRoles","DeleteRoles","ViewRoles"]
  },
  { 
    name: "Companies",
    link: "/admin/companies",
    icon: <Icon as={MdBusinessCenter} color='green.500' w={6} h={6}/>,
    permissions: ["ViewCompanies","EditCompanies","CreateCompanies","DeleteCompanies"]
  },
  {
    name: "CUSTOM_VALUES",
    link: "/admin/custom-values",
    icon: <Icon as={PiVaultFill} color='green.500' w={6} h={6}/>,
    permissions: []
  }
];

const WrapWithLayout = (page: () => JSX.Element): () => JSX.Element => () => <Layout
  links={NormalLinks}>{page()}</Layout>;
const WrapWithAdminLayout = (page: () => JSX.Element): () => JSX.Element => () => <IsAdmin><Layout
  links={AdminLinks}>{page()}</Layout></IsAdmin>;

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/">
      <Route path="/"  Component={Login}/>
      <Route path="/login" Component={Login} />
      <Route path="/dashboard" Component={WrapWithLayout(Dashboard)}/>
      <Route path="/provider" Component={WrapWithLayout(Provider)}/>
      <Route path="/categories" Component={WrapWithLayout(CategoriesPage)} />
      <Route path="/categories/:id" Component={WrapWithLayout(SingleCategoryPage)} />
      <Route path="/products" Component={WrapWithLayout(Products)}/>
      <Route path="/product/:id" Component={WrapWithLayout(Product)}/>
      <Route path="/inventories" Component={WrapWithLayout(Inventory)}/>
      <Route path="/bills" Component={WrapWithLayout(Bills)} />
      <Route path="/inventories/:id" Component={WrapWithLayout(PlaceOfBusiness)}/>

      {/* Admin routes*/}
      <Route path="/admin/roles" Component={WrapWithAdminLayout(Roles)}/>
      <Route path="/admin/users" Component={WrapWithAdminLayout(Users)}/>
      <Route path="/admin/companies" Component={WrapWithAdminLayout(Companies)} />
      <Route path="/admin/custom-values" Component={WrapWithAdminLayout(CustomValues)} />

      {/* Access control */}
      <Route path="/403" Component={() => <AccessRestrictedPage/>} />
      <Route path="/401" Component={() => <DontHavePermissionPage/>} />

    </Route>
  )
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
    <UserProvider>
      <ChakraProvider>
        <I18nextProvider i18n={i18n} defaultNS={'translations'}>
          <Suspense fallback={<div>Loading...</div>}>
            <RouterProvider router={router} />
          </Suspense>
        </I18nextProvider>
        <ToastContainer/>
      </ChakraProvider>
    </UserProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
